import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useThemeMediaQuery } from 'hooks/useThemeMediaQuery'
import { getNodeNameByType, License, LicenseStatus, LicenseType, trimMultipleSpaces } from '@mprolab/sdk'
import { Grid } from '@mui/material'
import * as Yup from 'yup'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import isNil from 'lodash/isNil'
import {
  Button,
  ButtonVariant,
  Flex,
  Input,
  KeyIcon,
  PopoverMenu,
  SingleMenuItem,
  Text,
  TextVariant,
  Tooltip,
  useModal
} from '@mprolab/uikit'
import { Check, Copy, Edit, MoreVertical, XCircle } from 'react-feather'
import TerminalStatus from './TerminalStatus'
import styled, { useTheme } from 'styled-components'
import dayjs from 'dayjs'
import CopyToClipboard from 'react-copy-to-clipboard'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { observer } from 'mobx-react-lite'
import { useReleaseTerminalMutation, useUpdateLicenseNameMutation } from 'state/services/indexNode/indexNode'
import { ProgressState, indexNodeStore } from 'mobxStores/IndexNodeStore'
import { useAwaitingWalletVerificationModal } from 'components/widgets/VerificationModals'
import { useMarketplaceContext } from 'utils/marketplace/useMarketplaceContext'
import { Send, Tag } from 'react-feather'
import TransferLicenseModal from './TransferLicenseModal'
import { getNodeStatusColor } from 'utils/getNodeStatusColor'
import useGetSingleAccessToken from 'hooks/useGetSingleAccessToken'
import { logMessage } from 'utils/logMessage'
import useToast from 'hooks/useToast'
import { IndexNodeApiKeyModal } from 'components/widgets/IndexNodeModal'
import TerminateNodeModal from 'components/widgets/TerminateNodeModal/TerminateNodeModal'

const StatusDot = styled(Flex)<{ color?: string }>`
  width: 10px;
  height: 10px;
  min-width: 10px;
  border-radius: 50%;
  background: ${({ color }) => color};
`
const StyledColumnText = styled(Text)`
  color: ${({ theme }) => theme.colors.gray6};
`

/** Green because it is a good state, not a warning: the licence is working
 *  for its owner on the marketplace. */
const ListedTag = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.green};
  background: ${({ theme }) => `${theme.colors.green}1a`};
  border: 1px solid ${({ theme }) => `${theme.colors.green}66`};
  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: ${({ theme }) => theme.colors.green};
  }
  &:hover {
    background: ${({ theme }) => `${theme.colors.green}2e`};
  }
`

const InfoContainer = styled(Flex)`
  max-width: 275px;
  padding-left: 24px;
  margin-top: 4px;
`

interface IIndexNodeRowProps {
  license: License
  licenses: License[]
  isLast: boolean
  refetchLicenses: () => void
  /** Set when this licence is currently on the marketplace. */
  listingId?: string
}

const MAX_INDEX_NODE_NAME_LENGTH = 40
const MIN_INDEX_NODE_NAME_LENGTH = 2

const IndexNodeRow: React.FC<IIndexNodeRowProps> = observer(
  ({ license, licenses, isLast, refetchLicenses, listingId }) => {
    const { t } = useTranslation()
    const { colors } = useTheme()
    const navigate = useNavigate()
    const marketplace = useMarketplaceContext()
    const isMobile = useThemeMediaQuery('mobile')
    const isTablet = useThemeMediaQuery('tablet')
    const { account } = useWeb3React()
    const { lastWorkingIpAddress, status, purchasedAt, acquiredAt, name, licenseId, type } = license
    const licenseName = isEmpty(name) ? licenseId : name

    const [hoveredName, setHoveredName] = useState<string | null>(null)
    const [editMode, setEditMode] = useState<{ [key: string]: boolean }>({})
    const [editedName, setEditedName] = useState<{ [key: string]: string }>({})
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [idCopied, setIdCopied] = useState(false)

    const [onPresentApiKeyModal] = useModal(
      <IndexNodeApiKeyModal {...{ name: licenseName, licenseId }} />,
      false,
      undefined,
      () => indexNodeStore.resetIndexNodeStore()
    )
    const [updateLicenseName] = useUpdateLicenseNameMutation()
    const [releaseTerminal, { isLoading: unbinding }] = useReleaseTerminalMutation()
    const { toastError, toastSuccess } = useToast()

    const { onPresentAwaitingVerificationModal, onModalClose } = useAwaitingWalletVerificationModal({})
    const { obtainAccessToken } = useGetSingleAccessToken()

    // ----- Handlers ----- //
    /**
     * Detach this licence from the Terminal holding it.
     *
     * The wallet signature is the whole authorisation — the licences service
     * checks that the signing wallet owns the licence before it unbinds
     * anything, so there is nothing to confirm in a dialog that the signature
     * prompt does not already confirm.
     *
     * A failure is surfaced, not swallowed: the holder pressed this because
     * they cannot sign in on another machine, and silence would leave them
     * pressing it again.
     */
    const unbindTerminal = async () => {
      onPresentAwaitingVerificationModal()
      setIsModalOpen(true)
      try {
        await obtainAccessToken()
        await releaseTerminal({
          account,
          accessToken: indexNodeStore.accessToken,
          licenseId: license.licenseId
        }).unwrap()
        refetchLicenses()
        onModalClose()
        setIsModalOpen(false)
        toastSuccess('Terminal unbound', 'This licence can now sign in on another machine.')
      } catch (error) {
        onModalClose()
        setIsModalOpen(false)
        logMessage(error, 'IndexNodeRow: unbind from Terminal')
        toastError('Could not unbind', 'The licence is still attached to its Terminal. Try again in a moment.')
      }
    }

    const handleEditClick = (licenseId: string): void => {
      formik.resetForm()
      setEditMode({ ...editMode, [licenseId]: true })
    }

    const handleSaveClick = async (licenseId: string, newName?: string) => {
      if (isNil(editedName[licenseId])) {
        setEditMode({ ...editMode, [licenseId]: false })
        return
      }

      onPresentAwaitingVerificationModal()
      setIsModalOpen(true)
      try {
        await obtainAccessToken()
        await updateLicenseName({
          account,
          accessToken: indexNodeStore.accessToken,
          licenseId,
          name: newName.trim()
        }).unwrap()

        refetchLicenses()
        onModalClose()
        setIsModalOpen(false)
        setEditMode({ ...editMode, [licenseId]: false })
        indexNodeStore.resetIndexNodeStore()
      } catch (error) {
        onModalClose()
        console.error(error)
      }
    }

    const formik = useFormik({
      enableReinitialize: true,
      initialValues: {
        name: licenseName,
        licenseId
      },
      validationSchema: Yup.object().shape({
        name: Yup.string()
          .required(t('licenses.errors.required'))
          .max(
            MAX_INDEX_NODE_NAME_LENGTH,
            t('licenses.errors.tooLong', { min: MIN_INDEX_NODE_NAME_LENGTH, max: MAX_INDEX_NODE_NAME_LENGTH })
          )
          .min(MIN_INDEX_NODE_NAME_LENGTH, t('licenses.errors.tooShort', { min: MIN_INDEX_NODE_NAME_LENGTH }))
          .matches(
            new RegExp('^([\\-\\w]+\\.?\\s?)+[\\-\\w]+\\.?$|^[-\\w]+\\.?$'),
            `${t('licenses.errors.validationSchema')}`
          )
      }),
      onSubmit: async (values) => {
        const { licenseId, name } = values
        await handleSaveClick(licenseId, name)
      }
    })

    const isNameDuplicate = (nameToCheck: string, licenseIdToExclude: string): boolean => {
      return licenses.some((otherLicense) => {
        return otherLicense.name === nameToCheck && otherLicense.licenseId !== licenseIdToExclude
      })
    }
    const nameAlreadyExist: boolean = isNameDuplicate(formik.values.name, licenseId)
    const nameError = !isEmpty(formik.errors.name)

    const renderNameField = useMemo(() => {
      const { licenseId } = license
      const isEditing = editMode[licenseId]

      switch (true) {
        case isEditing:
          return (
            <Flex fullWidth gap="16px">
              <Flex fullWidth flexDirection="column" gap="8px">
                <Input
                  focused
                  autoFocus
                  style={{ height: '32px' }}
                  value={formik.values.name}
                  onChange={(e) => {
                    const { value } = e.target
                    const trimmedValue = trimMultipleSpaces(value)
                    formik.setFieldValue('name', trimmedValue)
                    setEditedName({ ...editedName, [licenseId]: trimmedValue })
                  }}
                  onBlur={() => {
                    if (!isModalOpen) {
                      setEditMode({ ...editMode, [licenseId]: false })
                      formik.setErrors({})
                    }
                  }}
                  error={nameError}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') formik.submitForm()
                    if (e.key === 'Escape') {
                      setEditMode({ ...editMode, [licenseId]: false })
                      formik.setErrors({})
                    }
                  }}
                />
              </Flex>
              <Button
                size="sm"
                variant={ButtonVariant.SECONDARY}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => formik.submitForm()}
                disabled={nameError}
              >
                Save
              </Button>
            </Flex>
          )
        default:
          return (
            <Flex
              fullWidth
              justifyContent="space-between"
              gap="16px"
              style={{ cursor: 'pointer' }}
              onClick={() => handleEditClick(licenseId)}
            >
              <Text
                isTrimmed
                linesToDisplay={1}
                clickable
                variant={isMobile ? TextVariant.BODY_BIG_BOLD : TextVariant.BODY_DEFAULT}
              >
                {licenseName}
              </Text>
              {(isEqual(hoveredName, licenseId) || isMobile) && (
                <Flex style={{ cursor: 'pointer' }}>
                  <Edit color={colors.gray6} />
                </Flex>
              )}
            </Flex>
          )
      }
    }, [license, editMode, formik, hoveredName])

    const renderInfoMessages = (): JSX.Element => {
      return (
        <>
          {nameAlreadyExist && editMode[licenseId] && (
            <InfoContainer>
              <Text color={colors.gray6}>{t('licenses.alreadyInUse')}</Text>
            </InfoContainer>
          )}
          {nameError && (
            <InfoContainer>
              <Text color={colors.red}>{formik.errors.name}</Text>
            </InfoContainer>
          )}
        </>
      )
    }
    const [onPresentTerminateModal] = useModal(<TerminateNodeModal />, false, undefined, () =>
      indexNodeStore.resetIndexNodeStore()
    )
    // Which marketplace collection this licence's NFT belongs to. The licence
    // carries its own contract address, so prefer that and fall back to the
    // type mapping only for a licence that has no NFT yet.
    const sellCollection =
      marketplace.collections.find((c) => c.nft === license.nftContract?.toLowerCase()) ??
      marketplace.collections.find((c) => c.licenseType === license.type)

    /**
     * Only the MGA collection, not "any type whose name starts with mga_".
     *
     * Every licence type is prefixed `mga_` — mga_regular, mga_light, mga_inode
     * — so the previous `includes('mga')` matched all three and disabled the row
     * menu for every licence in the table. Nobody could open the licence key,
     * sell or transfer anything.
     */
    /**
     * This flag governs the licence key alone.
     *
     * It used to carry a second claim — that MGA trades like any other licence
     * "because its collection sits on the marketplace allowlist". That was
     * wrong, and wrong in an instructive way: the allowlist only says the
     * marketplace would accept the listing, while the NFT contract's own
     * `transferEnabled()` decides whether the token can move at all. MGA's is
     * false, so Sell and Transfer were offered on a licence that can never be
     * delivered.
     *
     * Nothing here gates that any more. MGA is simply absent from the
     * marketplace collections, so `sellCollection` is undefined and all three
     * trading actions disable themselves — see useMarketplaceContext.
     */
    const isMGA = license.type === LicenseType.MGA_REGULAR

    const [onPresentTransferModal] = useModal(
      <TransferLicenseModal
        nft={sellCollection?.nft ?? ''}
        collectionLabel={sellCollection?.label ?? ''}
        // This row IS the choice: opening from its menu and then asking which
        // licence the user meant discards what the click already said.
        initialTokenId={license.tokenId}
        onTransferred={refetchLicenses}
      />,
      false
    )

    const moreOptions: SingleMenuItem[] = [
      {
        label: 'Licence key',
        icon: <KeyIcon />,
        onClick: () => {
          obtainAccessToken()
          onPresentApiKeyModal()
        },
        disabled: isMGA
      },
      {
        label: 'Sell Licence',
        icon: <Tag style={{ stroke: colors.white }} />,
        onClick: () => {
          if (!sellCollection) return
          // Name the token, not just the collection. The row already knows
          // which licence it is; sending only the collection makes the seller
          // pick their licence again out of a list, from a page opened on one
          // specific row.
          navigate(
            license.tokenId
              ? `/marketplace/list/${sellCollection.nft}/${license.tokenId}`
              : `/marketplace/list/${sellCollection.nft}`
          )
        },
        // Nothing to sell while the licence is being set up or has been ended,
        // and nothing to sell into if this type has no collection configured.
        disabled: !sellCollection || status === LicenseStatus.INITIAL || status === LicenseStatus.TERMINATED
      },
      {
        label: 'Transfer Licence',
        icon: <Send style={{ stroke: colors.white }} />,
        onClick: () => onPresentTransferModal(),
        disabled: !sellCollection || status === LicenseStatus.INITIAL || status === LicenseStatus.TERMINATED
      },
      {
        /*
         * Detach the licence from the Terminal holding it.
         *
         * One licence runs one Terminal, so a holder changing machines is
         * refused on the second one until this happens. The endpoint has
         * existed since the binding did and nothing called it, which left the
         * only way out of a dead machine as asking somebody.
         *
         * Disabled when nothing is bound, rather than hidden: a menu whose
         * items move around is a menu people misclick, and greyed-out says
         * "nothing to release here" where absence says nothing at all.
         */
        label: 'Unbind from Terminal',
        icon: <XCircle style={{ stroke: colors.white }} />,
        onClick: () => void unbindTerminal(),
        disabled: !license.terminal?.bound || unbinding
      }
      // "Terminate" used to sit here. It set the licence status to TERMINATED in
      // the database and did nothing to the NFT — so a terminated licence went
      // on existing on chain, could still be transferred and still be sold,
      // while the platform stopped treating it as active. That split is a
      // leftover from the pre-NFT licensing model and is exactly what the
      // licence-to-token work removes elsewhere. Operator decision, 2026-08-30.
    ]

    const isActive = status !== LicenseStatus.TERMINATED

    return (
      <Grid container display="flex" alignItems="center">
        <Grid item xs={12} sm={2} mt={isMobile ? '16px' : '0px'}>
          <Flex justifyContent={isMobile ? 'space-between' : 'flex-start'} alignItems="center" gap="8px">
            {isMobile && <StyledColumnText>{t('licenses.systemId')}</StyledColumnText>}
            {/*
            The full id is 36 characters and would crowd out the name, so the
            row shows its head and hands over the whole value on click — it is
            an identifier to quote in a support thread, not something to read.
          */}
            <CopyToClipboard text={licenseId} onCopy={() => setIdCopied(true)}>
              <Flex alignItems="center" gap="6px" style={{ cursor: 'pointer' }} title={licenseId}>
                <StyledColumnText>{licenseId.slice(0, 8)}…</StyledColumnText>
                {idCopied ? <Check size={13} color={colors.green} /> : <Copy size={13} color={colors.gray6} />}
              </Flex>
            </CopyToClipboard>
          </Flex>
        </Grid>
        <Grid item xs={12} sm={2.2} mt={isMobile ? '16px' : '0px'}>
          <Flex
            gap="18px"
            minHeight="42px"
            alignItems="center"
            pr={isMobile ? '0px' : isTablet ? '16px' : '40px'}
            onMouseEnter={() => setHoveredName(license.licenseId)}
            onMouseLeave={() => setHoveredName(null)}
          >
            <StatusDot color={getNodeStatusColor(status)} />
            {renderNameField}
          </Flex>
          {isMobile && renderInfoMessages()}
        </Grid>
        <Grid item xs={12} sm={1.1} mt={isMobile ? '16px' : '0px'}>
          <Flex justifyContent="space-between">
            {isMobile && <StyledColumnText>{t('licenses.type')}</StyledColumnText>}
            <Text> {getNodeNameByType(type)}</Text>
          </Flex>
        </Grid>
        <Grid item xs={12} sm={1.4} mt={isMobile ? '8px' : '0px'}>
          <Flex justifyContent="space-between">
            {isMobile && <StyledColumnText>{t('licenses.createdDate')}</StyledColumnText>}
            <Text color={colors.purple}> {dayjs(purchasedAt).format('DD/MM/YYYY')}</Text>
          </Flex>
        </Grid>
        <Grid item xs={12} sm={1.4} mt={isMobile ? '8px' : '0px'}>
          <Flex justifyContent="space-between">
            {isMobile && <StyledColumnText>{t('licenses.purchaseDate')}</StyledColumnText>}
            {/*
            A licence that has never changed hands has no separate acquisition
            date, and for it the two ARE the same day: it was bought new.
          */}
            <Text color={colors.purple}> {dayjs(acquiredAt ?? purchasedAt).format('DD/MM/YYYY')}</Text>
          </Flex>
        </Grid>
        <Grid item xs={12} sm={1.3} mt={isMobile ? '8px' : '0px'}>
          <Flex justifyContent="space-between" alignItems="center">
            {isMobile && <StyledColumnText>{t('licenses.saleStatus')}</StyledColumnText>}
            {/*
            The answer to "which of my ten did I put up for sale?", which was
            previously a matter of matching token ids by eye. It links to the
            listing, because the next thing anybody wants is to look at it.
          */}
            {listingId ? (
              <ListedTag onClick={() => navigate(`/marketplace/listings/${listingId}`)}>
                {t('licenses.onSale')}
              </ListedTag>
            ) : (
              <Text color={colors.gray6}>—</Text>
            )}
          </Flex>
        </Grid>
        <Grid item xs={12} sm={1.1} mt={isMobile ? '8px' : '0px'}>
          <Flex justifyContent="space-between" alignItems="center">
            {isMobile && <StyledColumnText>Terminal</StyledColumnText>}
            <TerminalStatus terminal={license.terminal} />
          </Flex>
        </Grid>
        <Grid item xs={12} sm={1} mt={isMobile ? '16px' : '0px'}>
          <Flex justifyContent={isMobile ? 'space-between' : 'flex-end'} alignItems="center" gap="24px">
            <Flex gap="24px">
              {/*
              MGA used to be locked out of this menu entirely. Its collection
              is on the marketplace allowlist, so selling and transferring it
              has to work; only the licence key stays unavailable, and that is
              handled on the item itself.
            */}
              {
                <>
                  {status === LicenseStatus.TERMINATED ? (
                    <Tooltip title={t('licenses.licenseAlreadyTerminated')} customPadding="16px">
                      <MoreVertical color={colors.white} cursor="pointer" />
                    </Tooltip>
                  ) : (
                    <PopoverMenu
                      menuItems={moreOptions}
                      onItemClick={({ onClick }) => {
                        indexNodeStore.setNode(license)
                        indexNodeStore.setProgress(ProgressState.INITIAL)
                        onClick()
                      }}
                      customPaperStyle={{ minWidth: 131 }}
                      customBoxPadding="16px"
                      anchorOrigin={{
                        vertical: 34,
                        horizontal: 46
                      }}
                      transformOrigin={{
                        vertical: 'top',
                        horizontal: 'right'
                      }}
                    >
                      <MoreVertical color={colors.white} cursor="pointer" />
                    </PopoverMenu>
                  )}
                </>
              }
            </Flex>
          </Flex>
        </Grid>
        {!isMobile && renderInfoMessages()}
        {!isLast && <Flex m={isMobile ? '16px 0' : '8px 0'} fullWidth background={colors.gray7} height="1px" />}
      </Grid>
    )
  }
)

export default IndexNodeRow
